import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import MainLayout from "../../component/atoms/layout/mainLayout";
import PretendardText from "../../component/atoms/text/pretendardText";
import Header from "../../component/organisms/header";
import Sidebar from "../../component/organisms/sidebar";
import { route } from "../../router/route";
import { formatNumberWithCommas } from "./../../util/formatNumber";
import HomeChart from "./_components/homeChart";
import BoardApi from "../../api/board.api";
import moment from "moment";
import OrderApi from "../../api/order.api";
import AnalyticsApi from "../../api/analytics.api";
import getToday from "./../../util/getToday";
import ReviewApi from "../../api/review.api";
import { fileUrl } from "../../api/api";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { minToMs } from "../../util/formatMs";
import Loading from "../../component/templates/Loading";
import { errorHandler } from "../../util/errorHandler";

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;

  // ========== 지식상점 취소 요청 ========== //
  const getKnowledgeStoreCancelCount = async () => {
    const data = {
      page: 1,
      size: 0,
      orderStatus: "CANCEL_REQUEST",
      isMembership: "Inactive",
    };

    const response = (await OrderApi.getListOrder(data)).data.data;

    const count = response?.totalCount;

    return count || 0;
  };

  const {
    data: knowledgeStoreCancelCount = 0,
    isLoading: isKnowledgeStoreCancelCountLoading,
    isError: isKnowledgeStoreCancelCountError,
  } = useQuery({
    queryKey: ["knowledgeStoreCancelCountData", pathName],
    queryFn: getKnowledgeStoreCancelCount,
    onError: (error) => {
      errorHandler(error);
    },
    staleTime: minToMs(15),
  });

  // ========== 멤버십 상품 취소 요청 ========== //
  const getMembershipCancelCount = async () => {
    const data = {
      page: 1,
      size: 0,
      orderStatus: "CANCEL_REQUEST",
      isMembership: "Active",
    };

    const response = (await OrderApi.getListOrder(data)).data.data;

    const count = response?.totalCount;

    return count || 0;
  };

  const {
    data: membershipCancelCount = 0,
    isLoading: isMembershipCancelCountLoading,
    isError: isMembershipCancelCountError,
  } = useQuery({
    queryKey: ["membershipCancelCountData", pathName],
    queryFn: getMembershipCancelCount,
    onError: (error) => {
      errorHandler(error);
    },
    staleTime: minToMs(15),
  });

  // ========== 요약, 방문자, 기간별 분석 관련 ========== //
  const [summaryData, setSummaryData] = useState([]);
  const [visitorList, setVisitorList] = useState([]);
  const [periodAnalysisList, setPeriodAnalysisList] = useState([]);
  const [periodAnalysisWeekList, setPeriodAnalysisWeekList] = useState([]);

  const getPeriodAnalysisData = async () => {
    const today = getToday();

    const data = {
      page: 1,
      size: 0,
      searchDate: today,
      periodType: "day",
    };

    const response = (await AnalyticsApi.getListAnalytics(data)).data.data
      .content;

    return response || [];
  };

  const {
    data: periodAnalysisData = [],
    isLoading: isPeriodAnalysisDataLoading,
    isError: isPeriodAnalysisDataError,
  } = useQuery({
    queryKey: ["periodAnalysisDataData", pathName],
    queryFn: getPeriodAnalysisData,
    onError: (error) => {
      errorHandler(error);
    },
    staleTime: minToMs(15),
  });

  useEffect(() => {
    if (periodAnalysisData.length > 0) {
      console.log(periodAnalysisData[0]);
      // 요약
      setSummaryData(periodAnalysisData[0]);

      // 방문자 (10일치)
      const visitorProcessedResponse = periodAnalysisData.slice(0, 10);
      const visitorProcessedResponse2 = visitorProcessedResponse.map((item) => {
        return {
          date: moment(item.createdAt).format("YYYY-MM-DD"),
          visitor: item.visitCount,
          view: item.pageViewCount,
        };
      });

      setVisitorList(visitorProcessedResponse2);

      // 기간별 분석 리스트(일주일)
      const periodWeekProcessedResponse = periodAnalysisData.slice(0, 7);

      setPeriodAnalysisWeekList(periodWeekProcessedResponse);

      // 기간별 분석 리스트
      setPeriodAnalysisList(periodAnalysisData);
    }
  }, [periodAnalysisData]);

  // ========== 상품 후기 관련 ========== //
  const getReviewList = async () => {
    const data = {
      page: 1,
      size: 6,
    };

    const response = (await ReviewApi.getListReview(data)).data.data.content;

    const processedData = response.map((item, index) => {
      return {
        ...item,
        imageIsValid: true,
      };
    });

    const processedDataWithImages = await Promise.all(
      processedData.map(
        (item) =>
          new Promise((resolve) => {
            if (item.merchThumbnailServerFileName) {
              const img = new Image();
              img.src = `${fileUrl}/${item.merchThumbnailServerFileName}`;

              img.onload = () => {
                resolve({ ...item, imageIsValid: true });
              };

              img.onerror = () => {
                resolve({ ...item, imageIsValid: false });
              };
            } else {
              resolve(item);
            }
          })
      )
    );

    return processedDataWithImages || [];
  };

  const {
    data: reviewList = [],
    isLoading: isReviewListLoading,
    isError: isReviewListError,
  } = useQuery({
    queryKey: ["reviewListData", pathName],
    queryFn: getReviewList,
    onError: (error) => {
      errorHandler(error);
    },
    staleTime: minToMs(15),
  });

  // ========== 멤버십 QnA 컨텐츠 반응 관련 ========== //
  const getQnaList = async () => {
    const data = {
      page: 1,
      size: 6,
      boardPostType: "SECRET_QNA",
    };

    const response = (await BoardApi.getListBoard(data)).data.data.content;

    return response || [];
  };

  const {
    data: qnaList = [],
    isLoading: isQnaListLoading,
    isError: isQnaListError,
  } = useQuery({
    queryKey: ["qnaListData", pathName],
    queryFn: getQnaList,
    onError: (error) => {
      errorHandler(error);
    },
    staleTime: minToMs(15),
  });

  // ========== 커뮤니티 컨텐츠 반응 관련 ========== //
  const getCommunityList = async () => {
    const data = {
      page: 1,
      size: 6,
      boardPostType: "COMMUNITY",
    };

    const response = (await BoardApi.getListBoard(data)).data.data.content;

    return response || [];
  };

  const {
    data: communityList = [],
    isLoading: isCommunityListLoading,
    isError: isCommunityListError,
  } = useQuery({
    queryKey: ["communityListData", pathName],
    queryFn: getCommunityList,
    onError: (error) => {
      errorHandler(error);
    },
    staleTime: minToMs(15),
  });

  // ========== 1:1 문의 내역 관련 ========== //
  const getAskList = async () => {
    const data = {
      page: 1,
      size: 6,
      boardPostType: "ASK",
    };

    const response = (await BoardApi.getListBoard(data)).data.data.content;

    return response || [];
  };

  const {
    data: askList = [],
    isLoading: isAskListLoading,
    isError: isAskListError,
  } = useQuery({
    queryKey: ["askListData", pathName],
    queryFn: getAskList,
    onError: (error) => {
      errorHandler(error);
    },
    staleTime: minToMs(15),
  });

  return (
    <>
      <Header />
      <MainLayout>
        <Sidebar />
        {/* 본문 헤더 영역 */}
        <HomeWrapper>
          <HomeHeader>
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <PretendardText
                style={{
                  color: "#b5b5b5",
                  fontSize: "20px",
                  fontWeight: "600",
                  lineHeight: "34px",
                  letterSpacing: "-0.06px",
                }}
              >
                Welcome!
              </PretendardText>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <PretendardText
                  style={{
                    color: "#aaa",
                    fontSize: "40px",
                    fontWeight: "700",
                    lineHeight: "56px",
                    letterSpacing: "-0.28px",
                  }}
                >
                  안녕하세요. 관리자님 :)
                </PretendardText>
                <TotalCancelCountWrapper>
                  {isKnowledgeStoreCancelCountLoading ||
                  isMembershipCancelCountLoading ||
                  isKnowledgeStoreCancelCountError ||
                  isMembershipCancelCountError ? (
                    <div
                      style={{
                        width: 410,
                        height: 28,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Loading color="#3a96df" style={{ position: "static" }} />
                    </div>
                  ) : (
                    <>
                      {/* 지식상점 취소 요청 */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 14,
                        }}
                      >
                        <PretendardText
                          style={{
                            fontWeight: 700,
                            fontSize: 16,
                            lineHeight: "28px",
                            letterSpacing: "-0.3%",
                            color: "#aaa",
                          }}
                        >
                          지식상점 취소 요청
                        </PretendardText>
                        <TextDivider />
                        <PretendardText
                          style={{
                            fontWeight: 700,
                            fontSize: 16,
                            lineHeight: "28px",
                            letterSpacing: "-0.3%",
                            color: "#cf6679",
                          }}
                        >
                          {formatNumberWithCommas(knowledgeStoreCancelCount)}명
                        </PretendardText>
                      </div>

                      {/* 멤버십 상품 취소 요청 */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 14,
                        }}
                      >
                        <PretendardText
                          style={{
                            fontWeight: 700,
                            fontSize: 16,
                            lineHeight: "28px",
                            letterSpacing: "-0.3%",
                            color: "#aaa",
                          }}
                        >
                          멤버십 상품 취소 요청
                        </PretendardText>
                        <TextDivider />
                        <PretendardText
                          style={{
                            fontWeight: 700,
                            fontSize: 16,
                            lineHeight: "28px",
                            letterSpacing: "-0.3%",
                            color: "#cf6679",
                          }}
                        >
                          {formatNumberWithCommas(membershipCancelCount)}명
                        </PretendardText>
                      </div>
                    </>
                  )}
                </TotalCancelCountWrapper>
              </div>
            </div>
          </HomeHeader>

          {/* 요약 영역 */}
          <SummationContainer>
            <SummationHeader>
              <PretendardText
                style={{
                  color: "#f3f3f3",
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: "28px",
                  letterSpacing: "-0.3%",
                }}
              >
                요약
              </PretendardText>
            </SummationHeader>
            {isPeriodAnalysisDataLoading || isPeriodAnalysisDataError ? (
              <div
                style={{
                  width: "100%",
                  height: 214,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Loading color="#3a96df" style={{ position: "static" }} />
              </div>
            ) : (
              <>
                <div
                  style={{
                    width: "100%",
                    height: 107,
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                  }}
                >
                  <SummationItemWrapper>
                    <SummationItem>
                      <SummationItemTitle>신규 1년회원 수</SummationItemTitle>
                      <SummationItemCount>
                        {formatNumberWithCommas(summaryData?.annualCount || 0)}
                      </SummationItemCount>
                    </SummationItem>
                  </SummationItemWrapper>
                  <SummationItemWrapper>
                    <SummationItem>
                      <SummationItemTitle>신규 월간회원 수</SummationItemTitle>
                      <SummationItemCount>
                        {formatNumberWithCommas(summaryData?.monthCount || 0)}
                      </SummationItemCount>
                    </SummationItem>
                  </SummationItemWrapper>
                  <SummationItemWrapper>
                    <SummationItem>
                      <SummationItemTitle>
                        신규 지식상점 판매부수
                      </SummationItemTitle>
                      <SummationItemCount>
                        {formatNumberWithCommas(
                          summaryData?.storeSalesCount || 0
                        )}
                      </SummationItemCount>
                    </SummationItem>
                  </SummationItemWrapper>
                  <SummationItemWrapper
                    style={{
                      borderRight: "none",
                    }}
                  >
                    <SummationItem>
                      <SummationItemTitle>신규 회원가입 수</SummationItemTitle>
                      <SummationItemCount>
                        {formatNumberWithCommas(summaryData?.signupCount || 0)}
                      </SummationItemCount>
                    </SummationItem>
                  </SummationItemWrapper>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: 107,
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                  }}
                >
                  <SummationItemWrapper>
                    <SummationItem>
                      <SummationItemTitle>누적 1년회원 수</SummationItemTitle>
                      <SummationItemCount>
                        {formatNumberWithCommas(
                          summaryData?.totalAnnualCount -
                            summaryData?.totalAnnualCancelCount || 0
                        )}
                      </SummationItemCount>
                    </SummationItem>
                  </SummationItemWrapper>
                  <SummationItemWrapper>
                    <SummationItem>
                      <SummationItemTitle>누적 월간회원 수</SummationItemTitle>
                      <SummationItemCount>
                        {formatNumberWithCommas(
                          summaryData?.totalMonthCount || 0
                        )}
                      </SummationItemCount>
                    </SummationItem>
                  </SummationItemWrapper>
                  <SummationItemWrapper>
                    <SummationItem>
                      <SummationItemTitle>
                        누적 지식상점 판매부수
                      </SummationItemTitle>
                      <SummationItemCount>
                        {formatNumberWithCommas(
                          summaryData?.totalStoreSalesCount || 0
                        )}
                      </SummationItemCount>
                    </SummationItem>
                  </SummationItemWrapper>
                  <SummationItemWrapper
                    style={{
                      borderRight: "none",
                    }}
                  >
                    <SummationItem>
                      <SummationItemTitle>누적 회원가입 수</SummationItemTitle>
                      <SummationItemCount>
                        {formatNumberWithCommas(
                          summaryData?.totalSignupCount || 0
                        )}
                      </SummationItemCount>
                    </SummationItem>
                  </SummationItemWrapper>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: 107,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SummationItemWrapper>
                    <SummationItem>
                      <SummationItemTitle>총 매출액</SummationItemTitle>
                      <SummationItemCount>
                        {formatNumberWithCommas(summaryData?.totalRevenue || 0)}
                      </SummationItemCount>
                    </SummationItem>
                  </SummationItemWrapper>
                </div>
              </>
            )}
          </SummationContainer>

          {/* 방문자 통계 */}
          <VisitorContainer>
            <VisitorHeader>
              <PretendardText
                style={{
                  color: "#f3f3f3",
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: "28px",
                  letterSpacing: "-0.3%",
                }}
              >
                방문자
              </PretendardText>
            </VisitorHeader>
            {isPeriodAnalysisDataLoading || isPeriodAnalysisDataError ? (
              <div
                style={{
                  width: "100%",
                  height: 327,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Loading color="#3a96df" style={{ position: "static" }} />
              </div>
            ) : (
              <>
                <VisitorGraphWrapper>
                  <div
                    style={{
                      width: "fit-content",
                      height: 23,
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: "#b658ff",
                          borderRadius: "50%",
                        }}
                      />
                      <PretendardText
                        style={{
                          color: "#aaa",
                          fontSize: "14px",
                          fontWeight: 600,
                          lineHeight: "22.75px",
                        }}
                      >
                        방문자
                      </PretendardText>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: "#34f3ff",
                          borderRadius: "50%",
                        }}
                      />
                      <PretendardText
                        style={{
                          color: "#aaa",
                          fontSize: "14px",
                          fontWeight: 600,
                          lineHeight: "22.75px",
                        }}
                      >
                        페이지뷰
                      </PretendardText>
                    </div>
                  </div>
                </VisitorGraphWrapper>
                <HomeChart dataList={visitorList} />
              </>
            )}
          </VisitorContainer>

          {/* 기간별 분석 */}
          <AnalysisContainer>
            <AnalysisHeader>
              <PretendardText
                style={{
                  color: "#f3f3f3",
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: "28px",
                  letterSpacing: "-0.3%",
                }}
              >
                기간별 분석
              </PretendardText>
              <PretendardText
                style={{
                  color: "#aaa",
                  fontSize: "13px",
                  fontWeight: 500,
                  lineHeight: "18.75px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  const accessMenu = sessionStorage.getItem("accessMenu");

                  if (accessMenu?.includes("/statistics/sales")) {
                    navigate(route.statistics_sales);
                  } else {
                    toast("권한이 없습니다.");
                  }
                }}
              >
                더보기
              </PretendardText>
            </AnalysisHeader>
            {isPeriodAnalysisDataLoading || isPeriodAnalysisDataError ? (
              <div
                style={{
                  width: "100%",
                  height: 572,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Loading color="#3a96df" style={{ position: "static" }} />
              </div>
            ) : (
              <table
                style={{
                  width: "1044px",
                  height: "fit-content",
                  borderCollapse: "collapse",
                }}
              >
                <colgroup>
                  {/* 총합 : 1044px */}
                  {/* 일자 */}
                  <col width="111px" />
                  {/* 신규 1년회원 */}
                  <col width="145px" />
                  {/* 신규 월간회원 */}
                  <col width="112px" />
                  {/* 신규 지식상점 판매 */}
                  <col width="143px" />
                  {/* 매출액 */}
                  <col width="149px" />
                  {/* 방문자 */}
                  <col width="91px" />
                  {/* 가입 */}
                  <col width="79px" />
                  {/* 문의 */}
                  <col width="79px" />
                  {/* 후기 */}
                  <col width="135px" />
                </colgroup>
                <thead>
                  <tr
                    style={{
                      height: "41px",
                      background: "#313233",
                    }}
                  >
                    <th
                      style={{
                        paddingLeft: "30px",
                      }}
                    >
                      <AnalysisTableHeaderText
                        style={{
                          textAlign: "left",
                        }}
                      >
                        일자
                      </AnalysisTableHeaderText>
                    </th>
                    <th>
                      <AnalysisTableHeaderText>
                        신규 1년회원
                      </AnalysisTableHeaderText>
                    </th>
                    <th>
                      <AnalysisTableHeaderText>
                        신규 월간회원
                      </AnalysisTableHeaderText>
                    </th>
                    <th>
                      <AnalysisTableHeaderText>
                        신규 지식상점 판매
                      </AnalysisTableHeaderText>
                    </th>
                    <th>
                      <AnalysisTableHeaderText>매출액</AnalysisTableHeaderText>
                    </th>
                    <th>
                      <AnalysisTableHeaderText>방문자</AnalysisTableHeaderText>
                    </th>
                    <th>
                      <AnalysisTableHeaderText>가입</AnalysisTableHeaderText>
                    </th>
                    <th>
                      <AnalysisTableHeaderText>문의</AnalysisTableHeaderText>
                    </th>
                    <th style={{ paddingRight: "56px" }}>
                      <AnalysisTableHeaderText>후기</AnalysisTableHeaderText>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {periodAnalysisWeekList.map((item, index) => {
                    return (
                      <tr
                        key={index}
                        style={{
                          height: 59,
                          background: "transparent",
                          borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                        }}
                      >
                        <td>
                          <AnalysisTableDataItemText>
                            {moment(item.createdAt).format("YYYY-MM-DD")}
                          </AnalysisTableDataItemText>
                        </td>
                        <td>
                          <AnalysisTableDataItemText>
                            {formatNumberWithCommas(item?.annualCount || 0)}
                          </AnalysisTableDataItemText>
                        </td>
                        <td>
                          <AnalysisTableDataItemText>
                            {formatNumberWithCommas(item?.monthCount || 0)}
                          </AnalysisTableDataItemText>
                        </td>
                        <td>
                          <AnalysisTableDataItemText>
                            {formatNumberWithCommas(item?.storeSalesCount || 0)}
                          </AnalysisTableDataItemText>
                        </td>
                        <td>
                          <AnalysisTableDataItemText>
                            {formatNumberWithCommas(Number(item?.revenue) || 0)}
                          </AnalysisTableDataItemText>
                        </td>
                        <td>
                          <AnalysisTableDataItemText>
                            {formatNumberWithCommas(item?.visitCount || 0)}
                          </AnalysisTableDataItemText>
                        </td>
                        <td>
                          <AnalysisTableDataItemText>
                            {formatNumberWithCommas(item?.signupCount || 0)}
                          </AnalysisTableDataItemText>
                        </td>
                        <td>
                          <AnalysisTableDataItemText>
                            {formatNumberWithCommas(item?.askCount || 0)}
                          </AnalysisTableDataItemText>
                        </td>
                        <td style={{ paddingRight: "56px" }}>
                          <AnalysisTableDataItemText>
                            {formatNumberWithCommas(item?.reviewCount || 0)}
                          </AnalysisTableDataItemText>
                        </td>
                      </tr>
                    );
                  })}
                  {/* 최근 7일 합계 */}
                  <tr
                    style={{
                      height: 59,
                      background: "transparent",
                      borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                    }}
                  >
                    <td style={{ paddingLeft: "30px" }}>
                      <AnalysisTableDataTotalText
                        style={{
                          textAlign: "left",
                        }}
                      >
                        최근 7일 합계
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 신규 평생회원 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisWeekList.reduce(
                            (acc, cur) => acc + cur.annualCount,
                            0
                          )
                        )}
                        명
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 신규 1년회원 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisWeekList.reduce(
                            (acc, cur) => acc + cur.monthCount,
                            0
                          )
                        )}
                        명
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 신규 지식상점 판매 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisWeekList.reduce(
                            (acc, cur) => acc + cur.storeSalesCount,
                            0
                          )
                        )}
                        건
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 매출액 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisWeekList.reduce(
                            (acc, cur) => acc + Number(cur.revenue),
                            0
                          )
                        )}
                        원
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 방문자 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisWeekList.reduce(
                            (acc, cur) => acc + cur.visitCount,
                            0
                          )
                        )}
                        명
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 가입 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisWeekList.reduce(
                            (acc, cur) => acc + cur.signupCount,
                            0
                          )
                        )}
                        건
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 문의 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisWeekList.reduce(
                            (acc, cur) => acc + cur.askCount,
                            0
                          )
                        )}
                        건
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 후기 */}
                    <td style={{ paddingRight: "56px" }}>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisWeekList.reduce(
                            (acc, cur) => acc + cur.reviewCount,
                            0
                          )
                        )}
                        건
                      </AnalysisTableDataTotalText>
                    </td>
                  </tr>
                  {/* 이번달 합계 */}
                  <tr
                    style={{
                      height: 59,
                      background: "transparent",
                    }}
                  >
                    <td style={{ paddingLeft: "30px" }}>
                      <AnalysisTableDataTotalText
                        style={{
                          textAlign: "left",
                        }}
                      >
                        이번달 합계
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 신규 평생회원 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisList.reduce(
                            (acc, cur) => acc + cur.annualCount,
                            0
                          )
                        )}
                        명
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 신규 1년회원 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisList.reduce(
                            (acc, cur) => acc + cur.monthCount,
                            0
                          )
                        )}
                        명
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 신규 지식상점 판매 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisList.reduce(
                            (acc, cur) => acc + cur.storeSalesCount,
                            0
                          )
                        )}
                        건
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 매출액 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisList.reduce(
                            (acc, cur) => acc + Number(cur.revenue),
                            0
                          )
                        )}
                        원
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 방문자 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisList.reduce(
                            (acc, cur) => acc + cur.visitCount,
                            0
                          )
                        )}
                        명
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 가입 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisList.reduce(
                            (acc, cur) => acc + cur.signupCount,
                            0
                          )
                        )}
                        건
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 문의 */}
                    <td>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisList.reduce(
                            (acc, cur) => acc + cur.askCount,
                            0
                          )
                        )}
                        건
                      </AnalysisTableDataTotalText>
                    </td>
                    {/* 후기 */}
                    <td style={{ paddingRight: "56px" }}>
                      <AnalysisTableDataTotalText>
                        {formatNumberWithCommas(
                          periodAnalysisList.reduce(
                            (acc, cur) => acc + cur.reviewCount,
                            0
                          )
                        )}
                        건
                      </AnalysisTableDataTotalText>
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </AnalysisContainer>

          {/* 상품 후기 / 멤버십 QnA 컨텐츠 반응 / 커뮤니티 컨텐츠 반응 */}
          <div
            style={{
              width: 1044,
              height: "fit-content",
              minHeight: 565,
              display: "flex",
              gap: 15,
              marginBottom: 20,
            }}
          >
            {/* 상품 후기 */}
            <ReactionContainer>
              <ReactionHeader>
                <PretendardText
                  style={{
                    color: "#f3f3f3",
                    fontSize: "16px",
                    fontWeight: 700,
                    lineHeight: "28px",
                    letterSpacing: "-0.3%",
                  }}
                >
                  상품 후기
                </PretendardText>
                <PretendardText
                  style={{
                    color: "#aaa",
                    fontSize: "13px",
                    fontWeight: 500,
                    lineHeight: "18.75px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const accessMenu = sessionStorage.getItem("accessMenu");

                    if (accessMenu?.includes("/shopping/review")) {
                      navigate(route.shopping_review);
                    } else {
                      toast("권한이 없습니다.");
                    }
                  }}
                >
                  더보기
                </PretendardText>
              </ReactionHeader>
              {isReviewListLoading || isReviewListError ? (
                <div
                  style={{
                    width: "100%",
                    height: 510,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Loading color="#3a96df" style={{ position: "static" }} />
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "fit-content",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {reviewList.map((item, index) => {
                    return (
                      <div
                        key={index}
                        style={{
                          width: "100%",
                          height: 85,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "0px 16px",
                          borderBottom:
                            index === 5
                              ? "none"
                              : "0.5px solid rgba(255, 255, 255, 0.12)",
                        }}
                      >
                        {item.merchThumbnailServerFileName &&
                        item.imageIsValid ? (
                          <img
                            src={
                              item?.merchThumbnailServerFileName
                                ? `${fileUrl}/${item?.merchThumbnailServerFileName}`
                                : ""
                            }
                            alt="상품 이미지"
                            style={{
                              width: 48,
                              height: 48,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "48px",
                              height: "48px",
                              backgroundColor: "#333",
                            }}
                          />
                        )}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          <PretendardText
                            className="truncate"
                            style={{
                              width: 244,
                              color: "#aaa",
                              fontSize: "15px",
                              fontWeight: 400,
                              lineHeight: "24.75px",
                            }}
                          >
                            {item?.content || ""}
                          </PretendardText>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                marginRight: 8,
                              }}
                            >
                              {/* item.score의 0점부터 5점에 따라 이미지 설정정 */}
                              {[...Array(5)].map((_, index) => (
                                <img
                                  key={index}
                                  src={
                                    index < item?.score
                                      ? "/assets/admin/icons/ico_star_fill.svg"
                                      : "/assets/admin/icons/ico_star.svg"
                                  }
                                  alt="별점"
                                />
                              ))}
                            </div>
                            <PretendardText
                              style={{
                                color: "rgba(181, 181, 181, 0.5)",
                                fontSize: "14px",
                                fontWeight: 600,
                                lineHeight: "22.75px",
                                marginRight: 12,
                              }}
                            >
                              {item?.score || 0}.0
                            </PretendardText>
                            <PretendardText
                              className="truncate"
                              style={{
                                width: 95,
                                color: "rgba(181, 181, 181, 0.5)",
                                fontSize: "13px",
                                fontWeight: 500,
                                lineHeight: "18.75px",
                              }}
                            >
                              {item?.member?.nickname || ""}
                            </PretendardText>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ReactionContainer>

            {/* 멤버십 QnA 컨텐츠 반응 */}
            <ReactionContainer>
              <ReactionHeader>
                <PretendardText
                  style={{
                    color: "#f3f3f3",
                    fontSize: "16px",
                    fontWeight: 700,
                    lineHeight: "28px",
                    letterSpacing: "-0.3%",
                  }}
                >
                  멤버십 QnA 컨텐츠 반응
                </PretendardText>
                <PretendardText
                  style={{
                    color: "#aaa",
                    fontSize: "13px",
                    fontWeight: 500,
                    lineHeight: "18.75px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const accessMenu = sessionStorage.getItem("accessMenu");

                    if (accessMenu?.includes("/secret/qna")) {
                      navigate(route.secret_qna);
                    } else {
                      toast("권한이 없습니다.");
                    }
                  }}
                >
                  더보기
                </PretendardText>
              </ReactionHeader>
              {isQnaListLoading || isQnaListError ? (
                <div
                  style={{
                    width: "100%",
                    height: 510,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Loading color="#3a96df" style={{ position: "static" }} />
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "fit-content",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {qnaList.map((item, index) => {
                    return (
                      <div
                        key={index}
                        style={{
                          width: "100%",
                          height: 85,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          padding: "0px 16px",
                          gap: 3,
                          borderBottom:
                            index === 5
                              ? "none"
                              : "0.5px solid rgba(255, 255, 255, 0.12)",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          navigate(`${route.secret_qna_detail}/${item.id}`);
                        }}
                      >
                        <PretendardText
                          className="truncate"
                          style={{
                            width: "100%",
                            color: "#aaa",
                            fontSize: "15px",
                            fontWeight: 400,
                            lineHeight: "24.75px",
                          }}
                        >
                          {item?.title || ""}
                        </PretendardText>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <PretendardText
                            style={{
                              color: "rgba(181, 181, 181, 0.5)",
                              fontSize: "13px",
                              fontWeight: 500,
                              lineHeight: "18.75px",
                            }}
                          >
                            {moment(item.createdAt).format("YYYY-MM-DD")}
                          </PretendardText>
                          <PretendardText
                            className="truncate"
                            style={{
                              maxWidth: 95,
                              color: "rgba(181, 181, 181, 0.5)",
                              fontSize: "13px",
                              fontWeight: 500,
                              lineHeight: "18.75px",
                            }}
                          >
                            {item?.member?.nickname || ""}
                          </PretendardText>
                          <PretendardText
                            className="truncate"
                            style={{
                              color: "#aaa",
                              fontSize: "13px",
                              fontWeight: 500,
                              lineHeight: "18.75px",
                            }}
                          >
                            댓글{" "}
                            {formatNumberWithCommas(item?.commentCount || 0)}
                          </PretendardText>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ReactionContainer>

            {/* 커뮤니티 컨텐츠 반응 */}
            <ReactionContainer>
              <ReactionHeader>
                <PretendardText
                  style={{
                    color: "#f3f3f3",
                    fontSize: "16px",
                    fontWeight: 700,
                    lineHeight: "28px",
                    letterSpacing: "-0.3%",
                  }}
                >
                  커뮤니티 컨텐츠 반응
                </PretendardText>
                <PretendardText
                  style={{
                    color: "#aaa",
                    fontSize: "13px",
                    fontWeight: 500,
                    lineHeight: "18.75px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const accessMenu = sessionStorage.getItem("accessMenu");

                    if (accessMenu?.includes("/community/management")) {
                      navigate(route.community_management);
                    } else {
                      toast("권한이 없습니다.");
                    }
                  }}
                >
                  더보기
                </PretendardText>
              </ReactionHeader>
              {isCommunityListLoading || isCommunityListError ? (
                <div
                  style={{
                    width: "100%",
                    height: 510,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Loading color="#3a96df" style={{ position: "static" }} />
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "fit-content",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {communityList.map((item, index) => {
                    return (
                      <div
                        key={index}
                        style={{
                          width: "100%",
                          height: 85,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          padding: "0px 16px",
                          gap: 3,
                          borderBottom:
                            index === 5
                              ? "none"
                              : "0.5px solid rgba(255, 255, 255, 0.12)",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          navigate(
                            `${route.community_management_detail}/${item.id}`
                          );
                        }}
                      >
                        <PretendardText
                          className="truncate"
                          style={{
                            width: "100%",
                            color: "#aaa",
                            fontSize: "15px",
                            fontWeight: 400,
                            lineHeight: "24.75px",
                          }}
                        >
                          {item?.title || ""}
                        </PretendardText>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <PretendardText
                            style={{
                              color: "rgba(181, 181, 181, 0.5)",
                              fontSize: "13px",
                              fontWeight: 500,
                              lineHeight: "18.75px",
                            }}
                          >
                            {moment(item.createdAt).format("YYYY-MM-DD")}
                          </PretendardText>
                          <PretendardText
                            className="truncate"
                            style={{
                              maxWidth: 95,
                              color: "rgba(181, 181, 181, 0.5)",
                              fontSize: "13px",
                              fontWeight: 500,
                              lineHeight: "18.75px",
                            }}
                          >
                            {item?.member?.nickname || ""}
                          </PretendardText>
                          <PretendardText
                            className="truncate"
                            style={{
                              color: "#aaa",
                              fontSize: "13px",
                              fontWeight: 500,
                              lineHeight: "18.75px",
                            }}
                          >
                            댓글{" "}
                            {formatNumberWithCommas(item?.commentCount || 0)}
                          </PretendardText>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ReactionContainer>
          </div>

          {/* 1:1 문의 내역 */}
          <AskContainer>
            <AskHeader>
              <PretendardText
                style={{
                  color: "#f3f3f3",
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: "28px",
                  letterSpacing: "-0.3%",
                }}
              >
                1:1 문의 내역
              </PretendardText>
              <PretendardText
                style={{
                  color: "#aaa",
                  fontSize: "13px",
                  fontWeight: 500,
                  lineHeight: "18.75px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  const accessMenu = sessionStorage.getItem("accessMenu");

                  if (accessMenu?.includes("/operation/ask")) {
                    navigate(route.operation_ask);
                  } else {
                    toast("권한이 없습니다.");
                  }
                }}
              >
                더보기
              </PretendardText>
            </AskHeader>
            {isAskListLoading || isAskListError ? (
              <div
                style={{
                  width: "100%",
                  height: 59,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Loading color="#3a96df" style={{ position: "static" }} />
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "fit-content",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  // justifyContent: "center",
                  borderRadius: "10px",
                  background: "#272829",
                  boxShadow: "2px 2px 10px 0px rgba(0, 0, 0, 0.08)",
                }}
              >
                {askList.map((item, index) => {
                  return (
                    <div
                      key={index}
                      style={{
                        width: "100%",
                        height: 59,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0px 30px",
                        borderBottom:
                          askList.length - 1 === index
                            ? "none"
                            : "0.5px solid rgba(255, 255, 255, 0.12)",
                      }}
                    >
                      <PretendardText
                        className="truncate"
                        style={{
                          width: 902,
                          color: "#aaa",
                          fontSize: "14px",
                          fontWeight: 500,
                          lineHeight: "22px",
                          letterSpacing: "-0.3%",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          navigate(`${route.operation_ask_detail}/${item.id}`);
                        }}
                      >
                        {item?.title || ""}
                      </PretendardText>

                      <PretendardText
                        style={{
                          color: "#aaa",
                          fontSize: "14px",
                          fontWeight: 500,
                          lineHeight: "22px",
                          letterSpacing: "-0.3%",
                        }}
                      >
                        {item?.answerStatus ? "답변완료" : "미답변"}
                      </PretendardText>
                    </div>
                  );
                })}
              </div>
            )}
          </AskContainer>
        </HomeWrapper>
      </MainLayout>
    </>
  );
};

export default Home;

const HomeWrapper = styled.div.attrs((props) => {})`
  width: 1044px;
  height: auto;
  margin-top: 190px;
`;

const HomeHeader = styled.div.attrs((props) => {})`
  width: 1044px;
  height: 90px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 40px;
`;

const TotalCancelCountWrapper = styled.div.attrs((props) => {})`
  padding: 14px 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 50px;
  background: #272829;
  border-radius: 6px;
`;

const SummationContainer = styled.div.attrs((props) => {})`
  width: 1044px;
  height: fit-content;
  min-height: 270px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #272829;
  box-shadow: 2px 2px 10px 0px rgba(0, 0, 0, 0.08);
`;

const SummationHeader = styled.div.attrs((props) => {})`
  width: 100%;
  height: 59px;
  display: flex;
  align-items: center;
  padding: 0px 30px;
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.12);
`;

const SummationItemWrapper = styled.div.attrs((props) => {})`
  height: 107px;
  width: 261px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-right: 0.5px solid rgba(255, 255, 255, 0.12);
`;

const SummationItem = styled.div.attrs((props) => {})`
  width: 196px;
  height: 67px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummationItemTitle = styled(PretendardText).attrs((props) => {})`
  color: #aaa;
  font-size: 14px;
  font-weight: 600;
  line-height: 22.75px;
`;

const SummationItemCount = styled(PretendardText).attrs((props) => {})`
  color: #f3f3f3;
  font-size: 30px;
  font-weight: 700;
  line-height: 41.5px;
  letter-spacing: -0.7%;
`;

const VisitorContainer = styled.div.attrs((props) => {})`
  width: 1044px;
  height: fit-content;
  min-height: 439px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #272829;
  box-shadow: 2px 2px 10px 0px rgba(0, 0, 0, 0.08);
`;

const VisitorHeader = styled.div.attrs((props) => {})`
  width: 100%;
  height: 59px;
  display: flex;
  align-items: center;
  padding: 0px 30px;
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.12);
`;

const VisitorGraphWrapper = styled.div.attrs((props) => {})`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  padding: 24px 30px;
`;

const AnalysisContainer = styled.div.attrs((props) => {})`
  width: 1044px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #272829;
  box-shadow: 2px 2px 10px 0px rgba(0, 0, 0, 0.08);
`;

const AnalysisHeader = styled.div.attrs((props) => {})`
  width: 100%;
  height: 59px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 30px;
`;

const AnalysisTableHeaderText = styled(PretendardText).attrs((props) => {})`
  color: #aaa;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  line-height: 22.75px;
`;

const AnalysisTableDataItemText = styled(PretendardText).attrs((props) => {})`
  color: #aaa;
  text-align: right;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.3%;
`;

const AnalysisTableDataTotalText = styled(PretendardText).attrs((props) => {})`
  color: #f3f3f3;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  line-height: 22.75px;
`;

const AskContainer = styled.div.attrs((props) => {})`
  width: 1044px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #272829;
  box-shadow: 2px 2px 10px 0px rgba(0, 0, 0, 0.08);
`;

const AskHeader = styled.div.attrs((props) => {})`
  width: 100%;
  height: 59px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const ReactionContainer = styled.div.attrs((props) => {})`
  width: 338px;
  height: fit-content;
  min-height: 565px;
  display: flex;
  flex-direction: column;
  align-items: center;

  border-radius: 12px;
  background: #272829;
  box-shadow: 2px 2px 15px 0px rgba(0, 0, 0, 0.12);
`;

const ReactionHeader = styled.div.attrs((props) => {})`
  width: 100%;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const TextDivider = styled.span`
  height: 10px;
  width: 1px;
  background-color: #b8bfc4;
`;
